import React, { FC, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Image } from '@design-system/image';
import { Link } from '@design-system/link';
import { Typography } from '@design-system/typography';
import { useBrowserScreen } from '@root/hooks/useBrowserScreen';

import DecorationImage from '../DecorationImage';
import { SlideSceneProps } from '../models';
import RingWithProduct from '../RingWithProduct';
import WavyBackground from '../WavyBackground';

const SlideScene: FC<SlideSceneProps> = ({
  title,
  mainImage,
  mainImageAlt,
  link,
  bubble,
  decorations,
  bubbleBG,
  bgMobile,
  mainImageMobile,
}) => {
  const uid = useId();
  const { isMobileView, isTabletView } = useBrowserScreen();

  const wrapperRef = useRef<HTMLDivElement>(null);

  // ------ TRACK REAL SIZE OF WRAPPER ------
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!wrapperRef.current) return undefined;

    const update = () => {
      const rect = wrapperRef.current!.getBoundingClientRect();
      setSize({
        width: rect.width,
        height: rect.height,
      });
    };

    update(); // initial

    const observer = new ResizeObserver(update);
    observer.observe(wrapperRef.current);

    return () => observer.disconnect();
  }, []);

  // ------ COMPUTE RING CENTER + RADIUS ------
  const ring = useMemo(() => {
    const cx = size.width / 2;
    const cy = size.height / 2;
    const r = isMobileView ? size.width * 0.33 : size.width * 0.31;

    return { cx, cy, r };
  }, [size.width, size.height, isMobileView]);

  // ------ MOBILE ------
  const contentMobile = (
    <>
      <div className="products-slider-bubbles-item__product">
        <Image
          src={bgMobile}
          className="products-slider-bubbles-item__product-bg"
          role="presentation"
          objectFit="cover"
        />
        <Image
          src={mainImageMobile}
          className="products-slider-bubbles-item__product-ring"
          alt={mainImageAlt}
        />
      </div>

      <Typography
        className="products-slider-bubbles-item__title"
        variant="body"
        size="m"
        content={title ?? ''}
        wrapping="default"
      />
    </>
  );

  // ------ DESKTOP ------
  const contentDesktop = (
    <>
      <WavyBackground src={bubbleBG} amp={10} len={800} speed={4} />

      {decorations?.length
        ? decorations.map((d) => <DecorationImage key={`${uid}-${d.image}`} {...d} />)
        : null}

      {ring.cx > 0 && ring.cy > 0 && (
        <RingWithProduct
          cx={ring.cx}
          cy={ring.cy}
          r={ring.r}
          mainImage={mainImage}
          mainImageAlt={mainImageAlt}
          link={link}
          bubble={bubble}
        />
      )}

      <Typography
        className="products-slider-bubbles-item__title"
        variant="body"
        size="m"
        content={title ?? ''}
        wrapping="default"
      />
    </>
  );

  // ------ WRAPPER COMPONENT ------
  const Wrapper: React.ElementType = link ? Link : 'div';
  const wrapperProps = link ? { href: link, variant: 'wrapper' } : {};

  return (
    <Wrapper
      {...wrapperProps}
      ref={wrapperRef}
      className="products-slider-bubbles-item"
      style={{
        position: 'relative',
        width: isMobileView ? 360 : 800,
        height: isMobileView ? 390 : 520,
        margin: '0 auto',
      }}
    >
      {isMobileView || isTabletView ? contentMobile : contentDesktop}
    </Wrapper>
  );
};

export default SlideScene;
